/**
 * Trovva — My Day
 *
 * The agent's own workflow view. Clean, calm, phone-friendly.
 *   - Top: "Good morning Sarah — N tasks for today"
 *   - Today section: checkbox list grouped by lead
 *   - Overdue card (collapsed by default, red)
 *   - Upcoming (next 7 days, dimmer)
 *
 * Checkbox → PATCH /api/tasks with status=completed. Animates + strikes
 * through + moves to bottom of the list. "Undo" affordance for 5 sec.
 *
 * Accepts `user` from App.jsx. In demoMode, renders a mock-data view.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sun, CheckCircle2, Circle, ChevronDown, ChevronUp, AlertTriangle,
  CalendarDays, Loader2, RefreshCw, Plus, MessageSquare, Zap, Mic,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
// AskAI removed — Brain Chat is the single AI entry point

// Mock data for demoMode
function buildDemoTasks() {
  const today = new Date().toISOString().split('T')[0];
  const yday = addDays(today, -1);
  const twoAgo = addDays(today, -2);
  const tmr = addDays(today, 1);
  const d3 = addDays(today, 3);
  const d5 = addDays(today, 5);
  return [
    { id: 't1', title: 'Call Charles Xavier — preapproval check', due_date: today,  priority: 'high',   status: 'pending', lead: { name: 'Charles Xavier',  type: 'Buyer' } },
    { id: 't2', title: 'Follow-up email to Maria Rodriguez',        due_date: today,  priority: 'medium', status: 'pending', lead: { name: 'Maria Rodriguez', type: 'Seller' } },
    { id: 't3', title: 'Showing — 1 Bank St with James Wilson',     due_date: today,  priority: 'urgent', status: 'pending', lead: { name: 'James Wilson',    type: 'Buyer' } },
    { id: 't4', title: 'Listing prep — 85 Maple Ave',                due_date: today,  priority: 'medium', status: 'pending', lead: { name: 'Sarah Mitchell',  type: 'Seller' } },
    { id: 't5', title: 'Send MLS links to Sarah Mitchell',           due_date: yday,   priority: 'medium', status: 'pending', lead: { name: 'Sarah Mitchell',  type: 'Buyer' } },
    { id: 't6', title: 'Lock attorney review timeline',              due_date: twoAgo, priority: 'high',   status: 'pending', lead: { name: 'James Wilson',    type: 'Buyer' } },
    { id: 't7', title: 'Weekly touchpoint — David Chen',             due_date: tmr,    priority: 'low',    status: 'pending', lead: { name: 'David Chen',      type: 'Renter' } },
    { id: 't8', title: 'Staging consult — 400 Creek Rd',             due_date: d3,     priority: 'medium', status: 'pending', lead: { name: 'NJ Dental Group', type: 'Commercial' } },
    { id: 't9', title: 'Schedule inspection — 456 Cherry Hill',      due_date: d5,     priority: 'medium', status: 'pending', lead: { name: 'Maria Rodriguez', type: 'Seller' } },
  ];
}

export default function MyDayView({ user, demoMode }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOverdue, setShowOverdue] = useState(true);
  const [recentUndo, setRecentUndo] = useState(null); // { id, previousStatus }

  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || 'there';
  const today = new Date().toISOString().split('T')[0];

  const load = useCallback(async () => {
    setError(null);
    if (demoMode) {
      setTasks(buildDemoTasks());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch('/api/tasks?range=upcoming', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Fetch failed (${r.status})`);
      // Also fetch overdue (separate range since the 'upcoming' query excludes past dates)
      const r2 = await fetch('/api/tasks?range=overdue', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data2 = await r2.json();
      const combined = [...(data2.tasks || []), ...(data.tasks || [])];
      setTasks(combined);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [demoMode]);

  useEffect(() => { load(); }, [load]);

  // Group tasks into sections
  const sections = useMemo(() => {
    const byDate = { today: [], overdue: [], tomorrow: [], thisWeek: [], completedToday: [] };
    const tmr = addDays(today, 1);
    for (const t of tasks) {
      if (t.status === 'completed') {
        const when = (t.completed_at || '').slice(0, 10);
        if (when === today) byDate.completedToday.push(t);
        continue;
      }
      if (t.status === 'cancelled' || t.status === 'snoozed') continue;
      if (t.due_date < today) byDate.overdue.push(t);
      else if (t.due_date === today) byDate.today.push(t);
      else if (t.due_date === tmr) byDate.tomorrow.push(t);
      else if (t.due_date <= addDays(today, 7)) byDate.thisWeek.push(t);
    }
    // Sort each bucket: priority (urgent > high > medium > low) then date
    const prio = { urgent: 0, high: 1, medium: 2, low: 3 };
    for (const k of Object.keys(byDate)) {
      byDate[k].sort((a, b) => (prio[a.priority] ?? 2) - (prio[b.priority] ?? 2) || a.due_date.localeCompare(b.due_date));
    }
    return byDate;
  }, [tasks, today]);

  const markTask = async (taskId, newStatus) => {
    // Optimistic update
    const prev = tasks.find(t => t.id === taskId);
    if (!prev) return;
    setTasks(ts => ts.map(t => t.id === taskId ? {
      ...t,
      status: newStatus,
      completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
    } : t));
    if (newStatus === 'completed') {
      setRecentUndo({ id: taskId, previousStatus: prev.status });
      setTimeout(() => setRecentUndo(r => r?.id === taskId ? null : r), 5000);
    }

    if (demoMode) return; // nothing to persist

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch(`/api/tasks?id=${taskId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, completed_source: 'web' }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.error || `Update failed (${r.status})`);
      }
    } catch (e) {
      // Roll back
      setError(e.message);
      setTasks(ts => ts.map(t => t.id === taskId ? prev : t));
    }
  };

  if (loading) {
    return <CenterMsg><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading your day…</CenterMsg>;
  }

  const totalToday = sections.today.length;
  const totalOverdue = sections.overdue.length;

  return (
    <div style={{ padding: '20px 22px', maxWidth: 900, margin: '0 auto' }}>
      {/* Hero greeting */}
      <div style={{
        background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
        borderRadius: 16, padding: '18px 22px', marginBottom: 18, color: 'white',
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <Sun size={24} style={{ opacity: 0.85, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
            {greeting()} {firstName}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>
            {totalToday > 0 ? `${totalToday} task${totalToday !== 1 ? 's' : ''} for today` : 'No tasks scheduled for today'}
            {totalOverdue > 0 && <span> · <strong style={{ color: '#FECACA' }}>{totalOverdue} overdue</strong></span>}
          </div>
        </div>
        <button onClick={load} style={{
          background: 'rgba(255,255,255,0.18)', color: 'white', border: 'none',
          borderRadius: 8, padding: '7px 11px', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
        }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Brain Chat available via floating button */}

      {error && (
        <div style={{ marginBottom: 14, padding: 12, background: '#FEF2F2', color: theme.colors.danger, borderRadius: 10, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Whatsapp quick-tip card */}
      <WhatsAppTip />

      {/* Today */}
      {sections.today.length > 0 && (
        <Section title="Today" count={sections.today.length} icon={<Sun size={15} />}>
          {sections.today.map(t => <TaskRow key={t.id} task={t} onToggle={markTask} />)}
        </Section>
      )}

      {/* Overdue */}
      {sections.overdue.length > 0 && (
        <section style={{ marginBottom: 18 }}>
          <button onClick={() => setShowOverdue(v => !v)} style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', background: '#FEF2F2',
            border: `1px solid ${theme.colors.danger}30`,
            borderRadius: 10, cursor: 'pointer', textAlign: 'left',
          }}>
            <AlertTriangle size={16} color={theme.colors.danger} />
            <div style={{ flex: 1, fontWeight: 700, color: theme.colors.danger, fontSize: 13 }}>
              {sections.overdue.length} overdue — let's sweep these up
            </div>
            {showOverdue ? <ChevronUp size={16} color={theme.colors.danger} /> : <ChevronDown size={16} color={theme.colors.danger} />}
          </button>
          {showOverdue && (
            <div style={{ marginTop: 6, background: theme.colors.card, border: `1px solid ${theme.colors.danger}20`, borderRadius: 10 }}>
              {sections.overdue.map(t => <TaskRow key={t.id} task={t} onToggle={markTask} overdue />)}
            </div>
          )}
        </section>
      )}

      {/* Tomorrow */}
      {sections.tomorrow.length > 0 && (
        <Section title="Tomorrow" count={sections.tomorrow.length} icon={<CalendarDays size={15} />}>
          {sections.tomorrow.map(t => <TaskRow key={t.id} task={t} onToggle={markTask} dim />)}
        </Section>
      )}

      {/* Rest of week */}
      {sections.thisWeek.length > 0 && (
        <Section title="Later this week" count={sections.thisWeek.length} icon={<CalendarDays size={15} />}>
          {sections.thisWeek.map(t => <TaskRow key={t.id} task={t} onToggle={markTask} dim />)}
        </Section>
      )}

      {/* Completed today — celebratory footer */}
      {sections.completedToday.length > 0 && (
        <div style={{
          marginTop: 18, padding: 14,
          background: '#F0FDF4', border: `1px solid #BBF7D0`, borderRadius: 10,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <CheckCircle2 size={18} color={theme.colors.success} />
          <div style={{ fontSize: 13, color: theme.colors.text }}>
            <strong>{sections.completedToday.length} done today</strong> — nice work.
          </div>
        </div>
      )}

      {totalToday === 0 && totalOverdue === 0 && !loading && sections.thisWeek.length === 0 && sections.tomorrow.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight, fontSize: 14 }}>
          ✨ Clear schedule — a rare day. Log a new lead or check in with a past client.
        </div>
      )}

      {/* Undo toast */}
      {recentUndo && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          background: theme.colors.primaryDark, color: 'white', padding: '10px 16px',
          borderRadius: 20, fontSize: 12, fontWeight: 600, zIndex: 200,
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
          display: 'inline-flex', alignItems: 'center', gap: 10,
        }}>
          ✓ Marked done
          <button onClick={() => markTask(recentUndo.id, recentUndo.previousStatus)} style={{
            background: theme.colors.accent, color: 'white', border: 'none',
            padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, cursor: 'pointer',
          }}>Undo</button>
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────
function Section({ title, count, icon, children }) {
  return (
    <section style={{ marginBottom: 18 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        padding: '0 4px',
      }}>
        <span style={{ color: theme.colors.textLight }}>{icon}</span>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: theme.colors.textLight }}>
          {title} · {count}
        </div>
      </div>
      <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.border}`, borderRadius: 10, overflow: 'hidden' }}>
        {children}
      </div>
    </section>
  );
}

function TaskRow({ task, onToggle, overdue, dim }) {
  const done = task.status === 'completed';
  const prio = task.priority;
  const prioColor = prio === 'urgent' ? theme.colors.danger
                   : prio === 'high'  ? theme.colors.warning
                   : prio === 'low'   ? theme.colors.textLight
                   : theme.colors.primary;
  const prioLabel = prio?.toUpperCase() || 'MED';

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '11px 14px',
      borderBottom: `1px solid ${theme.colors.border}`,
      opacity: done ? 0.55 : (dim ? 0.82 : 1),
      transition: 'opacity 0.2s',
    }}>
      <button
        onClick={() => onToggle(task.id, done ? 'pending' : 'completed')}
        aria-label={done ? 'Mark pending' : 'Mark complete'}
        style={{
          flexShrink: 0, marginTop: 1,
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: done ? theme.colors.success : theme.colors.textLight,
          padding: 2,
        }}
      >
        {done ? <CheckCircle2 size={20} /> : <Circle size={20} />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 600, color: theme.colors.text,
          textDecoration: done ? 'line-through' : 'none',
          lineHeight: 1.35,
        }}>{task.title}</div>
        {(task.lead?.name || task.notes) && (
          <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 3, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {task.lead?.name && <span>{task.lead.name}{task.lead.type ? ` · ${task.lead.type}` : ''}</span>}
            {overdue && <span style={{ color: theme.colors.danger, fontWeight: 700 }}>· {task.due_date}</span>}
          </div>
        )}
      </div>
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 6,
        background: `${prioColor}18`, color: prioColor,
        textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
      }}>{prioLabel}</span>
    </div>
  );
}

function WhatsAppTip() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('trovva_mday_tip_dismissed') !== '1'; } catch { return true; }
  });
  const dismiss = () => {
    try { localStorage.setItem('trovva_mday_tip_dismissed', '1'); } catch { /* noop */ }
    setOpen(false);
  };
  if (!open) return null;
  return (
    <div style={{
      marginBottom: 18, padding: 14,
      background: `linear-gradient(135deg, #FFF7ED, #FFEDD5)`,
      border: `1px solid ${theme.colors.accent}30`, borderRadius: 12,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 17, flexShrink: 0,
        background: theme.colors.accent, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><MessageSquare size={16} /></div>
      <div style={{ flex: 1, fontSize: 12, color: theme.colors.text, lineHeight: 1.5 }}>
        <strong>Finish tasks from the road.</strong> Reply to your morning WhatsApp briefing with{' '}
        <code style={kbdStyle()}>Done 1 3</code> to close tasks, or voice-note{' '}
        <Mic size={11} style={{ verticalAlign: 'middle' }} /> "just wrapped the 2pm showing" and we'll match it.
      </div>
      <button onClick={dismiss} style={{
        background: 'transparent', border: 'none', color: theme.colors.textLight,
        cursor: 'pointer', padding: 6, fontSize: 11, fontWeight: 600,
      }}>Got it</button>
    </div>
  );
}

function CenterMsg({ children }) {
  return (
    <div style={{ padding: 60, textAlign: 'center', color: theme.colors.textLight, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      {children}
      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

function kbdStyle() {
  return {
    fontFamily: 'ui-monospace, monospace', fontSize: 11,
    padding: '1px 5px', borderRadius: 4, background: '#FCEEDC',
    color: '#9A3412', border: `1px solid ${theme.colors.accent}40`,
  };
}

function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().split('T')[0];
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning,';
  if (h < 17) return 'Good afternoon,';
  return 'Good evening,';
}
